import { Box, Button, HStack, Center, Divider, Spinner, VStack } from '@televet/kibble-ui';
import React, { useMemo, useState } from 'react';
import { WorkflowVisitType, WorkflowSendingHours, WorkflowEvent } from 'shared/types/graphql';
import AutomationGrid, { IGridContentProps } from './AutomationGrid';
import AutomationLine from './AutomationLine';

export interface WorkflowTemplate {
  id: string;
  isPublished: boolean;
  visitType: WorkflowVisitType;
  sendingHours: WorkflowSendingHours;
  name: string;
  event: WorkflowEvent | string;
}

type IAutomationTableProps = {
  workflowEventSettings: WorkflowTemplate[];
  workflowEventSettingsLoading: boolean;
  isAutomationHoursSet: boolean;
  hasPermission?: boolean;
};

interface IFilterButtonProps {
  isFilterActive: boolean;
  onSelectFilter: () => void;
  filterText: string;
}

enum ActiveFilters {
  All = 'all',
  Active = 'active',
  Inactive = 'inactive',
}

const FilterButton = ({ isFilterActive, onSelectFilter, filterText }: IFilterButtonProps): JSX.Element => {
  return (
    <Button
      size="xs"
      variant="tertiary"
      isActive={isFilterActive}
      onClick={(): void => onSelectFilter()}
      _active={{
        backgroundColor: 'background.primary',
        color: 'text.onContrast',
      }}
    >
      {filterText}
    </Button>
  );
};

const GridContent: IGridContentProps = {
  name: 'NAME',
  sendingHours: 'SENDING HOURS',
  event: 'EVENT',
  clientOrPet: 'CLIENT/PET',
  active: 'ACTIVE',
};

const AutomationTable = ({
  workflowEventSettings,
  workflowEventSettingsLoading,
  isAutomationHoursSet,
  hasPermission,
}: IAutomationTableProps): JSX.Element => {
  const [activeFilter, setActiveFilter] = useState(ActiveFilters.Active);

  const filteredWorkflowEventSettings = useMemo(() => {
    switch (activeFilter) {
      case ActiveFilters.All:
        return workflowEventSettings;
      case ActiveFilters.Active:
        return workflowEventSettings.filter(({ isPublished }) => isPublished);
      case ActiveFilters.Inactive:
        return workflowEventSettings.filter(({ isPublished }) => !isPublished);
      default:
        return workflowEventSettings;
    }
  }, [activeFilter, workflowEventSettings]);

  return (
    <Box py={6}>
      <HStack justify="flex-end" h={8}>
        <FilterButton
          isFilterActive={activeFilter === ActiveFilters.Active}
          onSelectFilter={(): void => setActiveFilter(ActiveFilters.Active)}
          filterText="Active"
        />
        <FilterButton
          isFilterActive={activeFilter === ActiveFilters.Inactive}
          onSelectFilter={(): void => setActiveFilter(ActiveFilters.Inactive)}
          filterText="Inactive"
        />
        <FilterButton
          isFilterActive={activeFilter === ActiveFilters.All}
          onSelectFilter={(): void => setActiveFilter(ActiveFilters.All)}
          filterText="All"
        />
      </HStack>
      <AutomationGrid textSize="xs" automationGridContent={GridContent} background="transparent" />
      <Divider mb={2} />
      {workflowEventSettingsLoading ? (
        <VStack h="400px" justify="center" align="center" mt={12}>
          <Spinner size="xl" />
        </VStack>
      ) : (
        filteredWorkflowEventSettings.length >= 1 &&
        filteredWorkflowEventSettings.map((workflowEventSetting, index) => {
          return (
            <AutomationLine
              hasPermission={hasPermission}
              key={workflowEventSetting.id}
              workflowEventSetting={workflowEventSetting}
              isAutomationHoursSet={isAutomationHoursSet}
              index={index}
            />
          );
        })
      )}
      {workflowEventSettings.length === 0 && !workflowEventSettingsLoading && (
        <Center mt={12}>
          Your clinic does not have any automations set up, please contact support for assistance.
        </Center>
      )}
      {filteredWorkflowEventSettings.length === 0 &&
        workflowEventSettings.length > 0 &&
        !workflowEventSettingsLoading && (
          <Center mt={12}>Your clinic does not have any {activeFilter} automations.</Center>
        )}
    </Box>
  );
};

export default AutomationTable;
